/**
 * Auth API Module
 * Методы для авторизации: login, logout, refresh-token, verify-email
 * 
 * @typedef {import('./types').LoginRequest} LoginRequest
 * @typedef {import('./types').TokenResponse} TokenResponse
 * @typedef {import('./types').VerifyEmailResponse} VerifyEmailResponse
 * @typedef {import('./types').PublicKeysResponse} PublicKeysResponse
 * @typedef {import('./types').RotateKeysResponse} RotateKeysResponse
 */

import { authFetch } from '../authClient';

/**
 * Базовый URL для Auth API
 * @returns {string}
 */
function getBaseUrl() {
  return import.meta.env.VITE_AUTH_API_URL ?? '';
}

/**
 * POST /api/auth/login
 * Авторизация пользователя по email и паролю (публичный endpoint)
 * Refresh token автоматически устанавливается в httpOnly cookie
 * @param {LoginRequest} credentials
 * @returns {Promise<TokenResponse>}
 */
export async function login(credentials) {
  const response = await fetch(`${getBaseUrl()}/api/auth/login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(credentials),
    credentials: 'include', // Для получения httpOnly cookie
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const error = new Error(errorData.message || 'Login failed');
    error.status = response.status;
    error.data = errorData;
    throw error;
  }

  const data = await response.json();
  console.log('[authApi] Login response:', { 
    hasAccessToken: !!data.accessToken, 
    hasAccess_token: !!data.access_token,
    hasCsrfToken: !!data.csrfToken,
    hasCsrf_token: !!data.csrf_token,
    expiresIn: data.expiresIn || data.expires_in
  });
  return data;
}

/**
 * POST /api/auth/refresh-token
 * Обновление access token (refresh token передаётся автоматически через httpOnly cookie)
 * @returns {Promise<TokenResponse>}
 */
export async function refreshToken() {
  console.log('[authApi] Refreshing token via httpOnly cookie...');
  
  const response = await fetch(`${getBaseUrl()}/api/auth/refresh-token`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    // Body пустой - refresh token передаётся через httpOnly cookie
    credentials: 'include',
  });

  console.log('[authApi] Refresh response status:', response.status);

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    console.error('[authApi] Refresh failed:', errorData);
    const error = new Error(errorData.message || 'Token refresh failed');
    error.status = response.status;
    error.data = errorData;
    throw error;
  }

  const data = await response.json();
  console.log('[authApi] Refresh success, got tokens:', !!data.accessToken || !!data.access_token);
  return data;
}

/**
 * POST /api/auth/logout
 * Выход из системы - инвалидация refresh token и очистка cookie
 * @returns {Promise<{status: string}>}
 */
export async function logout() {
  console.log('[authApi] Logging out...');
  
  const response = await fetch(`${getBaseUrl()}/api/auth/logout`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include', // Для отправки httpOnly cookie
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    console.error('[authApi] Logout failed:', errorData);
    // Не бросаем ошибку - даже при неудаче на сервере, очищаем локальное состояние
  }

  const data = await response.json().catch(() => ({ status: 'logged_out' }));
  console.log('[authApi] Logout response:', data);
  return data;
}

/**
 * GET /api/auth/verify-email
 * Подтверждение email адреса по токену из письма
 * @param {string} token
 * @returns {Promise<VerifyEmailResponse>}
 */
export async function verifyEmail(token) {
  return authFetch('/api/auth/verify-email', {
    params: { token },
  });
}

/**
 * GET /api/auth/public-key
 * Получить публичные ключи для проверки JWT
 * @returns {Promise<PublicKeysResponse>}
 */
export async function getPublicKeys() {
  return authFetch('/api/auth/public-key');
}

/**
 * POST /api/auth/rotate-keys
 * Ротация ключей (только admin)
 * @returns {Promise<RotateKeysResponse>}
 */
export async function rotateKeys() {
  return authFetch('/api/auth/rotate-keys', {
    method: 'POST',
  });
}
